import { useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Switch,
  Label,
} from '@alertwhale/ui'
import { CreditCard, Check, Loader2, Crown, Zap, Building2, Sparkles } from 'lucide-react'
import { formatNumber } from '@/lib/utils'
import { useBillingUsage } from '@/hooks/useBillingUsage'
import {
  usePackages,
  useCurrentSubscription,
  useUpgradePackage,
  formatLimit,
  formatPrice,
  type Package,
} from '@/hooks/usePackages'
import { toast } from 'sonner'

const PACKAGE_ICONS: Record<string, React.ReactNode> = {
  free: <Sparkles className="h-5 w-5" />,
  starter: <Zap className="h-5 w-5" />,
  pro: <Crown className="h-5 w-5" />,
  enterprise: <Building2 className="h-5 w-5" />,
}

function getPackageIcon(slug: string): React.ReactNode {
  return PACKAGE_ICONS[slug] || <Sparkles className="h-5 w-5" />
}

function PackageCard({
  pkg,
  isCurrentPackage,
  billingCycle,
  onUpgrade,
  isUpgrading,
}: {
  pkg: Package
  isCurrentPackage: boolean
  billingCycle: 'monthly' | 'yearly'
  onUpgrade: (packageId: string) => void
  isUpgrading: boolean
}) {
  const price = billingCycle === 'monthly' ? pkg.price_monthly : pkg.price_yearly
  const isRecommended = pkg.slug === 'pro'

  const features = [
    `${formatLimit(pkg.notifications_per_month)} notifications/month`,
    `${formatLimit(pkg.live_sessions_per_month)} live sessions/month`,
    `${formatLimit(pkg.api_calls_per_month)} API calls/month`,
    `${formatLimit(pkg.max_applications)} application${pkg.max_applications !== 1 ? 's' : ''}`,
    `${formatLimit(pkg.max_team_members)} team member${pkg.max_team_members !== 1 ? 's' : ''}`,
    ...(pkg.features || []),
  ]

  return (
    <Card className={isRecommended ? 'border-primary relative' : undefined}>
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="px-3">Recommended</Badge>
        </div>
      )}
      <CardHeader className="pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getPackageIcon(pkg.slug)}
            <CardTitle>{pkg.name}</CardTitle>
          </div>
          {isCurrentPackage && <Badge variant="secondary">Current</Badge>}
        </div>
        <div className="mt-4">
          {pkg.is_free ? (
            <span className="text-3xl font-bold">Free</span>
          ) : price === 0 ? (
            <span className="text-3xl font-bold">Custom</span>
          ) : (
            <>
              <span className="text-3xl font-bold">{formatPrice(price)}</span>
              <span className="text-muted-foreground">
                /{billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
            </>
          )}
          {billingCycle === 'yearly' && !pkg.is_free && price > 0 && (
            <p className="text-sm text-green-600 mt-1">
              Save {Math.round((1 - pkg.price_yearly / (pkg.price_monthly * 12)) * 100)}% annually
            </p>
          )}
        </div>
        {pkg.description && (
          <CardDescription className="mt-2">{pkg.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 mb-6">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <Button
          variant={isCurrentPackage ? 'outline' : isRecommended ? 'default' : 'outline'}
          className="w-full"
          disabled={isCurrentPackage || isUpgrading}
          onClick={() => onUpgrade(pkg.id)}
        >
          {isUpgrading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isCurrentPackage
            ? 'Current Plan'
            : pkg.slug === 'enterprise'
              ? 'Contact Sales'
              : 'Upgrade'}
        </Button>
      </CardContent>
    </Card>
  )
}

export function BillingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')

  const { data: usageData, isLoading: usageLoading } = useBillingUsage()
  const { data: packages, isLoading: packagesLoading } = usePackages()
  const { data: subscriptionData, isLoading: subscriptionLoading } = useCurrentSubscription()
  const upgradeMutation = useUpgradePackage()

  const usage = {
    notifications: usageData?.notifications || 0,
    liveSessions: usageData?.live_sessions || 0,
    apiCalls: usageData?.api_calls || 0,
  }

  const currentPackage = subscriptionData?.package
  const currentSubscription = subscriptionData?.subscription

  const quotas = currentPackage
    ? {
        notificationsPerMonth: currentPackage.notifications_per_month,
        liveSessionsPerMonth: currentPackage.live_sessions_per_month,
        apiCallsPerMonth: currentPackage.api_calls_per_month,
      }
    : {
        notificationsPerMonth: 1000,
        liveSessionsPerMonth: 100,
        apiCallsPerMonth: 10000,
      }

  const handleUpgrade = async (packageId: string) => {
    const pkg = packages?.find((p) => p.id === packageId)
    if (!pkg) return

    if (pkg.slug === 'enterprise') {
      window.location.href = 'mailto:sales@alertwhale.com?subject=Enterprise%20Plan%20Inquiry'
      return
    }

    try {
      const result = await upgradeMutation.mutateAsync({
        package_id: packageId,
        billing_cycle: billingCycle,
      })

      if (result.requires_payment && result.stripe_price_id) {
        toast.info('Redirecting to payment...')
        // TODO: Integrate with Stripe Checkout
      } else {
        toast.success(`Successfully upgraded to ${result.package.name}!`)
      }
    } catch (error) {
      toast.error('Failed to upgrade package. Please try again.')
    }
  }

  const isLoading = packagesLoading || subscriptionLoading

  // Calculate usage percentages, capping at 100%
  const getUsagePercent = (used: number, limit: number) => {
    if (limit === -1) return 0 // Unlimited
    return Math.min((used / limit) * 100, 100)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Billing</h1>
        <p className="text-muted-foreground">Manage your subscription and usage</p>
      </div>

      {/* Current Subscription Info */}
      {currentSubscription && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {currentPackage && getPackageIcon(currentPackage.slug)}
              {currentPackage?.name || 'Free'} Plan
            </CardTitle>
            <CardDescription>
              {currentSubscription.status === 'active' && 'Your subscription is active'}
              {currentSubscription.status === 'trialing' && 'You are on a free trial'}
              {currentSubscription.status === 'past_due' && (
                <span className="text-destructive">Payment is past due</span>
              )}
              {currentSubscription.status === 'canceled' && (
                <span className="text-muted-foreground">
                  Your subscription will end on{' '}
                  {currentSubscription.current_period_end
                    ? new Date(currentSubscription.current_period_end).toLocaleDateString()
                    : 'N/A'}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          {currentSubscription.current_period_end && (
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {currentSubscription.cancel_at_period_end
                  ? 'Cancels on'
                  : 'Next billing date'}
                : {new Date(currentSubscription.current_period_end).toLocaleDateString()}
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Current Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Current Usage</CardTitle>
          <CardDescription>Your usage this billing period</CardDescription>
        </CardHeader>
        <CardContent>
          {usageLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Notifications</p>
                <p className="text-2xl font-bold">
                  {formatNumber(usage.notifications)} /{' '}
                  {formatLimit(quotas.notificationsPerMonth)}
                </p>
                <div className="w-full bg-muted rounded-full h-2 mt-2">
                  <div
                    className={`h-2 rounded-full ${
                      getUsagePercent(usage.notifications, quotas.notificationsPerMonth) > 90
                        ? 'bg-destructive'
                        : 'bg-primary'
                    }`}
                    style={{
                      width: `${getUsagePercent(usage.notifications, quotas.notificationsPerMonth)}%`,
                    }}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Live Sessions</p>
                <p className="text-2xl font-bold">
                  {formatNumber(usage.liveSessions)} / {formatLimit(quotas.liveSessionsPerMonth)}
                </p>
                <div className="w-full bg-muted rounded-full h-2 mt-2">
                  <div
                    className={`h-2 rounded-full ${
                      getUsagePercent(usage.liveSessions, quotas.liveSessionsPerMonth) > 90
                        ? 'bg-destructive'
                        : 'bg-primary'
                    }`}
                    style={{
                      width: `${getUsagePercent(usage.liveSessions, quotas.liveSessionsPerMonth)}%`,
                    }}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">API Calls</p>
                <p className="text-2xl font-bold">
                  {formatNumber(usage.apiCalls)} / {formatLimit(quotas.apiCallsPerMonth)}
                </p>
                <div className="w-full bg-muted rounded-full h-2 mt-2">
                  <div
                    className={`h-2 rounded-full ${
                      getUsagePercent(usage.apiCalls, quotas.apiCallsPerMonth) > 90
                        ? 'bg-destructive'
                        : 'bg-primary'
                    }`}
                    style={{
                      width: `${getUsagePercent(usage.apiCalls, quotas.apiCallsPerMonth)}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-4">
        <Label
          htmlFor="billing-cycle"
          className={billingCycle === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}
        >
          Monthly
        </Label>
        <Switch
          id="billing-cycle"
          checked={billingCycle === 'yearly'}
          onCheckedChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
        />
        <Label
          htmlFor="billing-cycle"
          className={billingCycle === 'yearly' ? 'font-semibold' : 'text-muted-foreground'}
        >
          Yearly <Badge variant="secondary" className="ml-1">Save up to 20%</Badge>
        </Label>
      </div>

      {/* Plans */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Choose a Plan</h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {packages
              ?.filter((pkg) => pkg.is_active)
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  isCurrentPackage={currentPackage?.id === pkg.id}
                  billingCycle={billingCycle}
                  onUpgrade={handleUpgrade}
                  isUpgrading={upgradeMutation.isPending}
                />
              ))}
          </div>
        )}
      </div>

      {/* Payment Method */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Method</CardTitle>
          <CardDescription>Manage your payment information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-12 h-8 bg-muted rounded flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              {currentSubscription?.stripe_customer_id ? (
                <p className="text-sm">Payment method on file</p>
              ) : (
                <p className="text-sm text-muted-foreground">No payment method on file</p>
              )}
            </div>
            <Button
              variant="outline"
              className="ml-auto"
              onClick={() => toast.info('Stripe payment portal coming soon')}
            >
              <CreditCard className="h-4 w-4" />
              {currentSubscription?.stripe_customer_id
                ? 'Manage Payment Method'
                : 'Add Payment Method'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
